import React, { forwardRef } from 'react';
import { Label } from '@/components/atoms/Label';
import { Input } from '@/components/atoms/Form/Input';
import { FieldError } from 'react-hook-form';
import { SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import { Checkbox } from '@/components/atoms/Checkbox';
import {
    Container,
    Icon,
    Panel,
    PanelRow,
    Title,
} from '../../../../pages/Config/Webhooks/WebhookConfigurationPanel/Styles';
import { WebhookEventTypesConfig } from './Styles';
import { useBrandWebhookConfigurationPanelVM } from './BrandWebhookConfigurationPanel.viewmodel';
import { BrandWebhookConfigurationPanelProps, availableEventTypes } from './types';

type HeadersError = {
    name?: FieldError;
    value?: FieldError;
};

const BrandWebhookConfigurationPanel = (
    { webhook, setValid, setDirty }: BrandWebhookConfigurationPanelProps,
    ref: any,
) => {
    const { control, headers, isEditWebhook, toggleChannel, copyToClipboard, append, remove, Controller } =
        useBrandWebhookConfigurationPanelVM({ webhook, setValid, setDirty }, ref);

    return (
        <Container>
            <PanelRow style={{ paddingRight: !isEditWebhook ? '30px' : '' }}>
                <Controller
                    control={control}
                    name="webhookUrl"
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <Input
                            onChange={onChange}
                            data-testid="webhook_url"
                            onBlur={onBlur}
                            value={value}
                            labelId="webhook_url.label"
                            placeholder="webhook_url.placeholder"
                            error={error?.message}
                        />
                    )}
                />
                {isEditWebhook ? (
                    <>
                        <Input
                            data-testid="webhook_secret"
                            value={webhook?.webhookHmacSecret}
                            labelId="webhook_secret.label"
                            placeholder="webhook_secret.placeholder"
                            disabled={true}
                        />
                        <Icon
                            data-testid="clipboard.copy"
                            icon={SVGIcons.CLIPBOARD}
                            size={18}
                            marginTop="30px"
                            onClick={(e) => copyToClipboard(e, webhook!.webhookHmacSecret!)}
                        />
                    </>
                ) : null}
            </PanelRow>
            <Label labelId="webhook.headers.label" />
            {headers.map((headerField, index) => (
                <Controller
                    key={headerField.id}
                    control={control}
                    name={`additionalHeaders.${index}`}
                    render={({ field: { onChange, value: fieldData }, fieldState: { error } }) => (
                        <PanelRow role="header-row">
                            <Input
                                onChange={(e) => onChange({ ...fieldData, name: e.target.value })}
                                name={`headers.${index}.name`}
                                value={fieldData.name}
                                data-testid={`headers.${index}.name`}
                                placeholder="webhook.headers.name.placeholder"
                                error={(error as HeadersError)?.name?.message}
                            />
                            <Input
                                onChange={(e) => onChange({ ...fieldData, value: e.target.value })}
                                name={`headers.${index}.value`}
                                value={fieldData.value}
                                data-testid={`headers.${index}.value`}
                                placeholder="webhook.headers.value.placeholder"
                                error={(error as HeadersError)?.value?.message}
                            />
                            {index === headers.length - 1 ? (
                                <Icon
                                    data-testid="headers.add"
                                    icon={SVGIcons.PLUS}
                                    size={18}
                                    onClick={() => append({ name: '', value: '' })}
                                />
                            ) : (
                                <Icon
                                    data-testid={`headers.${index}.remove`}
                                    icon={SVGIcons.TRASH}
                                    color="red"
                                    size={18}
                                    onClick={() => remove(index)}
                                />
                            )}
                        </PanelRow>
                    )}
                />
            ))}
            <Label labelId="webhook.eventTypes.title" />
            <Panel>
                <Title type="small" weight="bold" text="webhook.eventTypes.subtitle" />
                <WebhookEventTypesConfig>
                    <Controller
                        control={control}
                        name="eventTypes"
                        render={({ field: { onChange, value } }) => (
                            <>
                                {availableEventTypes.map((eventType) => {
                                    const isChecked = (value as string[])?.includes(eventType.value) || false;
                                    return (
                                        <Checkbox
                                            key={eventType.value}
                                            data-testid={`eventType.${eventType.value}`}
                                            checked={isChecked}
                                            label={eventType.label}
                                            onChange={() => {
                                                const currentTypes = (value as string[]) || [];
                                                const newTypes = isChecked
                                                    ? currentTypes.filter((type) => type !== eventType.value)
                                                    : [...currentTypes, eventType.value];
                                                onChange(newTypes);
                                            }}
                                        />
                                    );
                                })}
                            </>
                        )}
                    />
                </WebhookEventTypesConfig>
            </Panel>
        </Container>
    );
};

export default forwardRef(BrandWebhookConfigurationPanel);
